import { cn } from "@/lib/utils"
import type { StepComponentProps } from "./step-contract"

/**
 * Soulmate Sketch multiple-choice step ("select all that apply" — Step6, Step12).
 * List rows with a trailing radio control: selected rows get the `main-purple`
 * border + fill and a filled check, matching the Figma. Wired via the quiz's
 * `stepOverrides["multiple-choice"]`; the shared component stays untouched.
 *
 * The radio is Figma-exact: unselected is a 1px `purple-light/20` ring; selected
 * is a `peach → purple-accent` gradient disc with the design's white check
 * (path `M1 4 L4 7 L10 1`), not a generic lucide tick.
 */
export function SoulmateMultiChoiceStep({ step, value, onChange }: StepComponentProps) {
  const selected = Array.isArray(value) ? value : []

  const toggle = (optionValue: string) => {
    if (selected.includes(optionValue)) {
      onChange(selected.filter((v) => v !== optionValue))
    } else if (step.maxSelect == null || selected.length < step.maxSelect) {
      onChange([...selected, optionValue])
    }
  }

  return (
    <div className="flex flex-col gap-2 [@media(max-height:740px)]:gap-1.5">
      {(step.options ?? []).map((option) => {
        const checked = selected.includes(option.value)
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => toggle(option.value)}
            className={cn(
              "flex h-14 w-full items-center justify-between gap-3 rounded-xl border-2 px-4 text-left text-base font-semibold backdrop-blur-[15px] transition-colors [@media(max-height:740px)]:h-12",
              checked
                ? "border-main-purple bg-main-purple/10 text-purple-light"
                : "border-transparent bg-purple-light/10 text-purple-light hover:bg-purple-light/15",
            )}
          >
            <span className="flex-1">{option.label}</span>
            <span
              className={cn(
                "flex size-6 shrink-0 items-center justify-center rounded-full transition-colors",
                checked
                  ? "bg-gradient-to-b from-peach to-purple-accent"
                  : "border border-purple-light/20",
              )}
            >
              {checked && (
                <svg
                  width="11"
                  height="8"
                  viewBox="0 0 11 8"
                  fill="none"
                  aria-hidden
                  className="text-white"
                >
                  <path
                    d="M1 4L4 7L10 1"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}
